function g(id, stepNumber, title, summary, sidebarPath, prerequisites, steps, tips) {
  return { id, stepNumber, title, summary, sidebarPath, prerequisites, steps, tips };
}

export const MODULE_SETUP_GUIDES = {
  'student-management': {
    guides: [
      g(
        'add-student-manually',
        1,
        'Adding a Student Manually',
        'Register a single student with full admission details, parent link, and portal login.',
        'People → Students → Add Student',
        ['Academic year selected', 'Class and section created', 'Classes mapped to subjects'],
        [
          {
            heading: 'Open the admission form',
            points: [
              'Go to People → Students and click Add Student.',
              'Select Academic Year, Class, and Section for the new admission.',
              'Enter Admission Number — must be unique across the school.',
            ],
          },
          {
            heading: 'Personal and academic details',
            points: [
              'Fill student name, gender, date of birth, phone, address, and optional email.',
              'Set roll number, admission date, blood group, religion, house, and previous school.',
              'Upload student photo and supporting documents where required.',
            ],
          },
          {
            heading: 'Parent / guardian link',
            points: [
              'Enter father, mother, or guardian contact details.',
              'System can create parent portal login linked to this student.',
              'Mark primary contact and emergency contact appropriately.',
            ],
          },
          {
            heading: 'Save and verify',
            points: [
              'Submit the form — validation checks duplicate admission numbers and phone formats.',
              'Student appears in Students List and All Students grid immediately.',
              'Open Student Details to confirm attendance, fees, and timetable tabs are ready.',
            ],
          },
        ],
        [
          'Use bulk Import Students when admitting an entire class at once.',
          'Admission number format should follow your school policy (e.g. DPS2026-X-A-001).',
        ]
      ),
      g(
        'student-directory-search',
        2,
        'Student Directory & Search',
        'Find, filter, and export students across classes using list and grid views.',
        'People → Students → Students List / All Students',
        ['Students enrolled in the system'],
        [
          {
            heading: 'List vs grid view',
            points: [
              'Students List provides a sortable table with class, section, roll, and status columns.',
              'All Students grid shows photo cards for quick visual identification.',
              'Both views support export to Excel, PDF, and print.',
            ],
          },
          {
            heading: 'Search and filters',
            points: [
              'Search by name, admission number, or roll number.',
              'Filter by class, section, gender, and active status.',
              'Use academic year selector in header to scope results to the current session.',
            ],
          },
          {
            heading: 'Quick actions',
            points: [
              'Click a row to open Student Details profile.',
              'Edit student from the action menu; update class or section when needed.',
              'Generate Bonafide or view fee status without leaving the list.',
            ],
          },
        ],
        ['Keep student status (Active / Left) updated for accurate dashboard counts.']
      ),
      g(
        'student-profile-records',
        3,
        'Student Profile & Records',
        'View attendance, fees, exams, leaves, timetable, and documents on one profile.',
        'People → Students → Student Details',
        ['At least one student enrolled'],
        [
          {
            heading: 'Open student profile',
            points: [
              'From Students List, click the student name or View Details.',
              'Profile header shows photo, class, section, admission number, and contact.',
            ],
          },
          {
            heading: 'Sub-record tabs',
            points: [
              'Attendance tab — day-wise history and percentage for the academic year.',
              'Fees tab — payable, paid, balance, and receipt history.',
              'Exam Results tab — marks and grades when published.',
              'Leaves, Time Table, and Library tabs show role-appropriate data.',
            ],
          },
          {
            heading: 'Documents and login',
            points: [
              'Upload and verify documents (Aadhar, transfer certificate, etc.).',
              'View or reset student portal login details where permitted.',
              'Link additional guardians if family structure requires it.',
            ],
          },
        ],
        ['Parents see a read-only subset of this data in the Parent Portal for their linked child.']
      ),
      g(
        'promotion-lifecycle',
        4,
        'Promotion & Lifecycle',
        'Promote students to the next class, handle leave school, and rejoin workflows.',
        'People → Students → Student Promotion',
        ['New academic year created', 'Target classes exist for promoted students'],
        [
          {
            heading: 'Year-end promotion',
            points: [
              'Open Student Promotion after the current session ends.',
              'Select source class/section and target class/section for the new academic year.',
              'Review student list and confirm promotion — creates lifecycle ledger entry.',
            ],
          },
          {
            heading: 'Leave school',
            points: [
              'Mark student as Left with reason and date when they transfer out.',
              'Historical records remain for reports and audits.',
            ],
          },
          {
            heading: 'Rejoin',
            points: [
              'Rejoin workflow restores an inactive student with new class assignment.',
              'Previous academic history stays linked on the profile.',
            ],
          },
        ],
        ['Run promotion only after the new academic year is set as current in the header selector.']
      ),
      g(
        'bonafide-certificates',
        5,
        'Bonafide Certificates',
        'Generate official bonafide PDF documents for students on request.',
        'People → Students → Bonafide',
        ['Student active in system', 'School profile configured in Settings'],
        [
          {
            heading: 'Generate bonafide',
            points: [
              'Open Bonafide from the Students menu or student profile actions.',
              'Search and select the student.',
              'Preview certificate with school letterhead, student name, class, and purpose.',
              'Download or print PDF for parent submission.',
            ],
          },
        ],
        ['Configure school name and logo in Settings → School Settings for correct letterhead.']
      ),
    ],
  },

  'admission-management': {
    guides: [
      g(
        'log-admission-enquiry',
        1,
        'Logging Admission Enquiries',
        'Capture new admission leads with contact details and class preference.',
        'Academic → Enquiries',
        ['Academic year selected', 'Target classes created'],
        [
          {
            heading: 'Create enquiry',
            points: [
              'Open Academic → Enquiries and click Add Enquiry.',
              'Enter student name, gender, date of birth, parent name, mobile, and email.',
              'Select target class, enquiry source (Website, Walk-in, Referral), and status.',
              'Add notes describing how the family heard about the school.',
            ],
          },
        ],
        ['Log every phone call and walk-in — even if not yet confirmed — to measure conversion rates.']
      ),
      g(
        'enquiry-follow-ups',
        2,
        'Enquiry Follow-ups',
        'Schedule callbacks, add remarks, and track enquiry status over time.',
        'Academic → Enquiries → Enquiry detail',
        ['At least one enquiry logged'],
        [
          {
            heading: 'Add follow-up',
            points: [
              'Open an enquiry from the list to view full detail.',
              'Add Follow-up with remarks (e.g. “Sent prospectus”, “Fee structure shared”).',
              'Set Next Follow-up Date for the admissions desk reminder.',
              'Update status: Open → In Progress → Converted or Closed.',
            ],
          },
        ],
        ['Filter enquiries by status and date to prioritize callbacks each morning.']
      ),
      g(
        'convert-enquiry-student',
        3,
        'Converting Enquiry to Student',
        'Turn a successful enquiry into a live admission without re-entering data.',
        'Academic → Enquiries → Convert',
        ['Enquiry marked In Progress or ready to convert'],
        [
          {
            heading: 'Convert workflow',
            points: [
              'From enquiry detail, click Convert to Student.',
              'Name, phone, class preference, and parent details pre-fill the Add Student form.',
              'Complete remaining mandatory fields (admission number, section, documents).',
              'On save, enquiry status becomes Converted and student appears in Students List.',
            ],
          },
        ],
        ['Share fee structure during follow-up so conversion is faster at registration day.']
      ),
      g(
        'admission-pipeline-review',
        4,
        'Admission Pipeline Review',
        'Monitor enquiry volume, conversion rate, and source effectiveness.',
        'Academic → Enquiries (list filters)',
        ['Multiple enquiries logged over time'],
        [
          {
            heading: 'Review pipeline',
            points: [
              'Filter by date range, status, source, and target class.',
              'Export enquiry list for management meetings.',
              'Compare Converted vs Closed to refine marketing and admission process.',
            ],
          },
        ],
        ['Track source field consistently — helps identify which channels bring quality leads.']
      ),
    ],
  },

  'attendance-tracking': {
    guides: [
      g(
        'attendance-setup',
        1,
        'Preparing for Daily Attendance',
        'Ensure classes, sections, and teacher assignments exist before marking begins.',
        'Academic → Classes → Class Sections',
        ['Academic year current', 'Students enrolled per class-section', 'Teachers assigned'],
        [
          {
            heading: 'Prerequisites checklist',
            points: [
              'Students must be assigned to class and section for the academic year.',
              'Teachers should have class assignments for their permitted attendance scope.',
              'School holidays can be configured under HRM to exclude non-working days.',
            ],
          },
        ],
        ['Mark attendance on the first school day to establish baseline percentage on dashboards.']
      ),
      g(
        'mark-student-attendance',
        2,
        'Marking Student Attendance',
        'Record Present, Absent, or Late for an entire class on a given date.',
        'HRM → Attendance → Student Attendance',
        ['Students enrolled', 'Teacher or admin role'],
        [
          {
            heading: 'Daily marking',
            points: [
              'Select class, section, and date (defaults to today).',
              'Mark each student Present, Absent, or Late.',
              'Use bulk actions to mark all Present then adjust exceptions.',
              'Save — attendance percentage updates on student and parent dashboards.',
            ],
          },
        ],
        ['Teachers see only their assigned classes; admins can mark school-wide.']
      ),
      g(
        'staff-attendance',
        3,
        'Staff Attendance Register',
        'Track non-teaching and administrative staff attendance separately.',
        'HRM → Attendance → Staff Attendance',
        ['Staff records in HR module'],
        [
          {
            heading: 'Staff register',
            points: [
              'Select date and mark Present / Absent / Leave for each staff member.',
              'Record check-in and check-out times where applicable.',
              'Export monthly staff attendance for payroll processing.',
            ],
          },
        ],
        ['Link staff attendance to payslip module for integrated HR workflows.']
      ),
      g(
        'leave-approval',
        4,
        'Leave Requests & Approval',
        'Students and staff apply for leave; approvers review and sync with attendance.',
        'HRM → Approve Request',
        ['Leave types configured'],
        [
          {
            heading: 'Leave workflow',
            points: [
              'Students/parents apply under Leave & Attendance in their portal.',
              'Staff apply from My Leave & Attendance.',
              'Approvers open Approve Request, review reason and dates, approve or reject.',
              'Approved leave automatically reflects in attendance reports.',
            ],
          },
        ],
        ['Rejected leaves do not affect attendance — student remains marked Absent if not present.']
      ),
      g(
        'attendance-reports',
        5,
        'Attendance Reports',
        'Export class-wise and student-wise attendance analytics.',
        'Report → Attendance Report / Class Report',
        ['Attendance marked for the period'],
        [
          {
            heading: 'Generate reports',
            points: [
              'Filter by academic year, class, section, and date range.',
              'View daily register, monthly summary, and percentage columns.',
              'Export to Excel or PDF for parent meetings and compliance.',
            ],
          },
        ],
        ['Low attendance alerts on parent dashboard help reduce drop-offs early.']
      ),
    ],
  },

  'fees-management': {
    overviewIntro:
      'SkoolWeb uses a layered Fee Engine. Start with taxonomy (categories, heads, types), build class configurations, assign charges to students, then collect, reconcile, and report — all from Management → Fees Collection and Settings → Fee Settings.',
    guides: [
      g(
        'payment-modes-fee-taxonomy',
        1,
        'Payment Modes, Categories & Fee Heads',
        'Configure how payments are recorded and how fee heads are organised before building class structures.',
        'Settings → Payment Modes · Settings → Fee Settings',
        ['Headmaster / Admin login', 'Academic year selected in header'],
        [
          {
            heading: 'Payment modes',
            points: [
              'Go to Settings → Payment Modes.',
              'Add each mode your counter uses: Cash, UPI, Cheque, Bank Transfer, Card, etc.',
              'Active modes appear in the Collect Fees payment dropdown.',
            ],
          },
          {
            heading: 'Fee categories',
            points: [
              'Open Settings → Fee Settings → Fee Categories.',
              'Create categories with a short code and name (e.g. ACAD — Academic, TRN — Transport).',
              'Activate or deactivate categories; codes cannot be changed after creation.',
              'Export list to Excel, PDF, or print for records.',
            ],
          },
          {
            heading: 'Fee heads',
            points: [
              'Open Fee Heads under the same Fee Settings menu.',
              'Link each head to a category — e.g. Tuition, Development, Exam under Academic.',
              'Mark Optional when the charge applies only to some students (transport, hostel, etc.).',
              'Heads feed into fee configurations and discount rules.',
            ],
          },
        ],
        [
          'Set up payment modes first — collection cannot proceed without at least one active mode.',
          'Use consistent category codes — they appear in reports and Fee Engine Insights.',
        ]
      ),
      g(
        'setup-fee-types',
        2,
        'Setting Up Fee Types',
        'Create the global fee type catalog reused across all classes and configurations.',
        'Settings → Fee Settings → Fees Type',
        ['Fee categories and heads configured'],
        [
          {
            heading: 'Add fee types',
            points: [
              'Open Settings → Fee Settings → Fees Type.',
              'Click Add — enter name, code, and description for each fee (Tuition, Transport, Library, Exam, etc.).',
              'Mark Active / Inactive; inactive types are hidden from new configurations.',
            ],
          },
          {
            heading: 'Manage the list',
            points: [
              'Filter by status: All, Active, or Inactive.',
              'Edit or delete types not yet used in a fee configuration.',
              'Usage count column shows how many configurations reference each type.',
              'Export to Excel, PDF, or print.',
            ],
          },
        ],
        ['Fee type names should match what parents see on receipts and the parent portal.']
      ),
      g(
        'build-fees-group',
        3,
        'Building Fee Configurations (Fees Group)',
        'Create class-wise fee structures with line items, due dates, late fees, and optional installments.',
        'Management → Fees Collection → Fees Group',
        ['Fees types created', 'Classes configured for the academic year'],
        [
          {
            heading: 'Create a fee configuration',
            points: [
              'Open Management → Fees Collection → Fees Group.',
              'Click Add — enter configuration name, select class (or all classes), and set due date.',
              'Add fee line items: pick fee type, enter amount, mark compulsory or optional.',
              'Review compulsory total vs optional total before saving.',
            ],
          },
          {
            heading: 'Late fee rules',
            points: [
              'Choose late fee type: None, Fixed amount, or Percentage of outstanding.',
              'Set fine amount and frequency (e.g. per month after due date).',
              'Late fees accrue when Run Jobs is executed from Fee Engine Insights.',
            ],
          },
          {
            heading: 'Installments (optional)',
            points: [
              'On a saved configuration, click Manage Installments.',
              'Generate equal splits across 1–24 installments with a first due date and monthly interval.',
              'Edit individual installment name, due date, and amount.',
              'Delete installments only if no payments exist against them.',
              'Workflow: Generate → edit amounts → Assign/Sync students → Collect Fees.',
            ],
          },
          {
            heading: 'List actions',
            points: [
              'Table shows group name, class, fee items breakdown, total, late fee, installment count, and due date.',
              'Edit, delete, or export configurations to Excel/PDF for board approval.',
            ],
          },
        ],
        [
          'Create one configuration per class per academic year — different classes can have different totals.',
          'Verify academic year in the header before adding configurations.',
        ]
      ),
      g(
        'fees-master-catalog',
        4,
        'Fees Master Catalog',
        'View and manage individual fee line items generated from configurations — amounts, fines, and status.',
        'Settings → Fee Settings → Fees Master',
        ['At least one Fees Group configuration saved'],
        [
          {
            heading: 'Browse fee master lines',
            points: [
              'Open Settings → Fee Settings → Fees Master.',
              'Each row shows fees group, fee type, due date, amount, fine type/value, and compulsory/optional flag.',
              'Filter by fees group, fee type, fine type, status, and date range.',
            ],
          },
          {
            heading: 'Bulk operations',
            points: [
              'Select multiple rows for bulk delete when cleaning up old academic year data.',
              'Add or edit individual lines via the shared fee modal.',
              'Export filtered list to Excel, PDF, or print.',
            ],
          },
        ],
        [
          'Fees Master is the line-item view of what Assign / Sync pushes to students.',
          'Use filters to audit a single class or fee type before term start.',
        ]
      ),
      g(
        'discount-rules-scholarships',
        5,
        'Discount Rules & Scholarships',
        'Create percentage, fixed, or full-waiver rules and assign them to students.',
        'Management → Fees Collection → Discount Rules',
        ['Fee heads or categories configured'],
        [
          {
            heading: 'Create a discount rule',
            points: [
              'Open Management → Fees Collection → Discount Rules.',
              'Click Add — enter rule name, type (Percentage, Fixed amount, or Full waiver), and value.',
              'Set Applies To: All charges, Fee head, Category, or Student-specific.',
              'Set validity from/to dates and active status.',
            ],
          },
          {
            heading: 'Assign to a student',
            points: [
              'Click Assign on a rule row — search and select the student.',
              'Assigned discounts apply when charges are synced or at collection time.',
              'View all rules in the table with type, value, validity, and active badge.',
            ],
          },
          {
            heading: 'Export',
            points: [
              'Export discount rules list to Excel, PDF, or print for scholarship records.',
            ],
          },
        ],
        [
          'Use Full waiver for 100% scholarship students; Percentage for partial concessions.',
          'Set validity dates to auto-expire seasonal discounts.',
        ]
      ),
      g(
        'assign-sync-students',
        6,
        'Assign & Sync Student Charges',
        'Generate fee ledger entries for students — bulk assign or per-student re-sync.',
        'Management → Fees Collection → Assign / Sync',
        ['Fee configurations created', 'Students enrolled in target class/section'],
        [
          {
            heading: 'Bulk assign',
            points: [
              'Open Assign / Sync → click Add (bulk assign).',
              'Filter by class — only classes with a fee configuration appear.',
              'Select fee master lines and target students.',
              'Confirm assign — system creates open charges on each student ledger.',
            ],
          },
          {
            heading: 'Per-student re-sync',
            points: [
              'Click Re-sync on a student row to rebuild their charges from current configuration.',
              'Toggle optional fee groups on or off for that student.',
              'Use after changing fee configuration mid-year or when optional transport/hostel applies.',
            ],
          },
          {
            heading: 'Filters & export',
            points: [
              'Filter assignments by class, section, gender, and category.',
              'Delete incorrect assignments before re-syncing.',
              'Export assignment list to Excel, PDF, or print.',
            ],
          },
        ],
        [
          'Always Assign / Sync after creating or editing Fees Group configurations.',
          'Re-sync one student before bulk re-sync when testing optional fee toggles.',
        ]
      ),
      g(
        'collect-fee-payments',
        7,
        'Collecting Fee Payments',
        'Search students, view aging and balances, record multi-line partial payments, and print receipts.',
        'Management → Fees Collection → Collect Fees',
        ['Student charges assigned/synced', 'Payment modes configured'],
        [
          {
            heading: 'Summary dashboard',
            points: [
              'Top cards show: Total demanded, Collected, Outstanding, Defaulters count.',
              'Aging buckets: Current, 1–30 days, 31–60 days, 61–90 days, 90+ days overdue.',
              'Click an aging button to filter the student list instantly.',
            ],
          },
          {
            heading: 'Student list & filters',
            points: [
              'Columns: Admission no, Roll, Student, Class, Amount, Due date, Aging, Balance, Status.',
              'Status badges: Paid (green), Partial (amber), Unpaid (red), No Fees.',
              'Filter by admission no, roll no, class, section, status, and date range.',
              'Export list to Excel, PDF, or print.',
            ],
          },
          {
            heading: 'Collection modal',
            points: [
              'Click Collect Fees on a row with outstanding balance.',
              'Select individual fee lines or use All Outstanding / All Compulsory shortcuts.',
              'Enter payment amount (supports partial pay across multiple lines).',
              'Choose payment mode, payment date, reference/receipt number, and remarks.',
              'Apply wallet balance (Advance Used) if the student has credit.',
              'Save — system generates a PDF receipt with gross, discount, waiver, and fine breakdown.',
            ],
          },
          {
            heading: 'Student fee ledger (admin)',
            points: [
              'Rows with zero balance show View Details — opens the student fee ledger.',
              'Alternatively: People → Students → open a student → Fees tab — same ledger, payment history, and receipt print.',
            ],
          },
        ],
        [
          'Select the correct academic year in the header before opening Collect Fees.',
          'Partial payments update balance immediately — parents see live status in portal.',
        ]
      ),
      g(
        'student-wallet-credits',
        8,
        'Student Wallet & Credits',
        'Credit advance payments to a student wallet and apply balance at future collections.',
        'Management → Fees Collection → Wallet',
        ['Students enrolled'],
        [
          {
            heading: 'View balances',
            points: [
              'Open Management → Fees Collection → Wallet.',
              'Table shows each student’s current wallet balance.',
              'Export to Excel, PDF, or print.',
            ],
          },
          {
            heading: 'Credit wallet',
            points: [
              'Click Credit on a student row — enter amount and remarks.',
              'Wallet balance increases immediately.',
              'At Collect Fees, tick Apply Wallet to deduct from balance before cash/UPI payment.',
            ],
          },
          {
            heading: 'Transaction history',
            points: [
              'Click a student to view wallet transaction log: type, amount, balance after, reference, date.',
              'Voiding a receipt that used wallet restores the credited amount.',
            ],
          },
        ],
        [
          'Use wallet for advance fee deposits — reduces cash handling at term start.',
          'Wallet credits are per student per academic year context.',
        ]
      ),
      g(
        'fee-adjustments-waivers',
        9,
        'Fee Adjustments & Manual Waivers',
        'Review discount, waiver, and fine entries; apply manual waivers to open charges.',
        'Management → Fees Collection → Adjustments',
        ['Student charges exist on ledger'],
        [
          {
            heading: 'Adjustments log',
            points: [
              'Open Management → Fees Collection → Adjustments.',
              'Filter: All, DISCOUNT, WAIVER, or FINE.',
              'Table shows date, type, student, fee head, amount, reason, and linked discount rule.',
              'Export to Excel, PDF, or print.',
            ],
          },
          {
            heading: 'Manual waiver',
            points: [
              'Click Add Waiver — search student, select open charge, enter waiver amount and reason.',
              'Waiver reduces outstanding balance on that charge line.',
              'Appears in adjustments log and on student fee ledger.',
            ],
          },
        ],
        [
          'Manual waivers are for one-off concessions not covered by a standing discount rule.',
          'All adjustments are auditable — do not delete; use void receipt for payment corrections.',
        ]
      ),
      g(
        'receipt-history-voiding',
        10,
        'Receipt History & Voiding',
        'Reprint receipts, track confirmed vs voided status, and reverse incorrect collections.',
        'Management → Fees Collection → Receipt History',
        ['At least one fee collection recorded'],
        [
          {
            heading: 'Receipt list',
            points: [
              'Open Management → Fees Collection → Receipt History.',
              'Columns: Receipt no, Date, Student, Payment mode, Amount, Wallet used, Status.',
              'Status: CONFIRMED (active) or VOIDED (reversed).',
              'Filter by date range and status.',
            ],
          },
          {
            heading: 'Print receipt',
            points: [
              'Click Print on a confirmed receipt — downloads PDF with full payment breakdown.',
              'Receipt shows gross, discounts, waivers, fines, wallet applied, and net paid.',
            ],
          },
          {
            heading: 'Void a receipt',
            points: [
              'Click Void on a confirmed receipt — enter optional reason.',
              'System reverses payment allocations and restores wallet if wallet was used.',
              'Voided receipts remain in history for audit; student balance returns to outstanding.',
            ],
          },
          {
            heading: 'Export',
            points: [
              'Export receipt history to Excel, PDF, or print for daily cash reconciliation.',
            ],
          },
        ],
        [
          'Void only incorrect same-day entries — use adjustments for partial corrections.',
          'Match daily receipt totals with Fees Report before bank deposit.',
        ]
      ),
      g(
        'fee-reports-analytics',
        11,
        'Fee Reports & Fee Engine Insights',
        'Student-level reports and multi-tab analytics for collections, defaulters, and compliance.',
        'Report → Fees Report · Report → Fee Engine Insights',
        ['Collections and assignments recorded'],
        [
          {
            heading: 'Fees Report',
            points: [
              'Open Report → Fees Report.',
              'Per-student columns: Admission, Roll, Student, Class, Section, Total due, Paid, Balance, Last payment, Status.',
              'Filter by class, section, status, and date range.',
              'Click student name to open Student Details.',
              'Export to Excel, PDF, or print.',
            ],
          },
          {
            heading: 'Fee Engine Insights — tab overview',
            points: [
              'Outstanding by Module — academic, transport, library, hostel breakdown.',
              'Collections — totals by payment mode and wallet usage.',
              'Installment Compliance — pending, overdue, and paid installments.',
              'Head Wise — demanded, collected, outstanding per fee head.',
              'Defaulters — students with any outstanding balance.',
              'Discounts & Waivers — summary and detail tables.',
              'Daily Collection — receipts and amounts per day.',
            ],
          },
          {
            heading: 'Run Jobs',
            points: [
              'Click Run Jobs on Fee Engine Insights to accrue late fees and rebuild summary totals.',
              'Run after due dates pass or at month-end reconciliation.',
            ],
          },
          {
            heading: 'Dashboard widgets',
            points: [
              'Headmaster dashboard shows fee collection chart, outstanding, fines, and defaulter count.',
              'Administrative dashboard links directly to Collect Fees.',
            ],
          },
        ],
        [
          'Use Daily Collection tab for end-of-day cash tally with Receipt History.',
          'Defaulters tab helps follow up before report-card or exam withholding.',
        ]
      ),
      g(
        'parent-student-portal-fees',
        12,
        'Parent & Student Portal — Fees',
        'How parents and students view dues, pay online, and print receipts from their portal.',
        'Academic → Fees (Parent / Student / Guardian portal)',
        ['Student charges assigned', 'Parent account linked to student'],
        [
          {
            heading: 'Fee ledger view',
            points: [
              'Parent or student logs in → Academic → Fees.',
              'Table shows each charge: module source (Academic, Transport, Library, Hostel), fee type, due date, amount, paid, balance, discount, fine, status.',
              'Wallet balance displayed at top when credit exists.',
            ],
          },
          {
            heading: 'Payment history',
            points: [
              'Scroll to Payment History section — lists past receipts with date, amount, and mode.',
              'Click Print on a receipt to download PDF.',
            ],
          },
          {
            heading: 'Pay online',
            points: [
              'Pay Online button visible to parent, guardian, and student roles when payment gateway is configured.',
              'Select outstanding lines → proceed to payment → confirm order.',
              'Balance updates immediately after successful payment.',
            ],
          },
          {
            heading: 'Dashboard reminders',
            points: [
              'Parent dashboard shows pending fee widget and next installment due date.',
              'Notifications route to Fees page when fee is due or paid.',
            ],
          },
        ],
        [
          'Teachers cannot access the student Fees page — redirect applies automatically.',
          'Share institute number + parent login credentials during admission.',
        ]
      ),
    ],
  },

  'examination-system': {
    overviewIntro:
      'Examinations support Offline and Online delivery plus optional Internal Assessment. Start with Exam Types → Exams → Timetable Setup, then Question Bank/Papers for online, marks or Online Evaluation, and Publish.',
    guides: [
      g(
        'exam-types-and-exams',
        1,
        'Exam Types & Creating Exams',
        'Define exam categories and create class exams with optional Internal Assessment.',
        'Academic → Examinations → Exam Types · Exams',
        ['Academic year selected', 'Classes and subjects configured'],
        [
          {
            heading: 'Exam Types',
            points: [
              'Open Academic → Examinations → Exam Types.',
              'Add types such as Unit Test, Mid-Term, Final, Pre-Board.',
              'Keep types active for use when creating exams.',
            ],
          },
          {
            heading: 'Create an exam',
            points: [
              'Open Exams → Add — enter name, select exam type and classes.',
              'Optionally enable Has Internal Assessment for theory + internal marks.',
              'Save as Draft; publish when the exam is ready for school-wide use.',
              'Click Manage / Timetable on a row to open subject schedule setup.',
            ],
          },
        ],
        [
          'Enable Internal Assessment before building the timetable so internal max/pass fields appear.',
          'Publish the exam only after the timetable is verified.',
        ]
      ),
      g(
        'exam-timetable-offline-online',
        2,
        'Timetable Setup — Offline & Online',
        'Configure each subject slot as Offline or Online with marks and optional paper.',
        'Exams → Manage → Exam Timetable Setup',
        ['Exam created', 'Class subjects ready'],
        [
          {
            heading: 'Open timetable setup',
            points: [
              'From Exams list, open Manage / Timetable for the exam.',
              'Path: /academic/exam-schedule (Exam Timetable Setup).',
              'Set date, start/end time, classroom (offline), max marks, and passing marks per subject.',
            ],
          },
          {
            heading: 'Offline vs Online mode',
            points: [
              'Mode Offline — classroom exam; room optional; no question paper required.',
              'Mode Online — requires an active Question Paper for that subject; instructions optional.',
              'Schedule view for everyone: Academic → Examinations → Schedule.',
            ],
          },
          {
            heading: 'Internal fields (when enabled)',
            points: [
              'Enter Theory Max/Pass and Internal Max/Pass.',
              'Choose pass policy: total only, or need to pass both theory and internal.',
              'Turning Internal off later folds marks into a single theory column.',
            ],
          },
        ],
        [
          'Online schedules with student attempts cannot be freely edited or removed.',
          'Exam dates on save cannot be in the past.',
        ]
      ),
      g(
        'internal-assessment-marks',
        3,
        'Internal Assessment & Marks Entry',
        'Enter theory and internal marks and apply pass rules.',
        'Academic → Examinations → Exam Result → Assign Marks',
        ['Exam with Internal Assessment enabled', 'Timetable saved'],
        [
          {
            heading: 'How Internal works',
            points: [
              'Internal is an exam-level toggle — not a separate exam type.',
              'Total marks = theory + internal (absolute marks, not percentage weightage).',
              'Absent students skip mark validation for that subject.',
            ],
          },
          {
            heading: 'Enter marks',
            points: [
              'Open Exam Result → select exam, class, section, subject → Assign Marks.',
              'Enter Theory and Internal for each student (or mark Absent).',
              'System validates against max marks and pass thresholds.',
              'Save — blocked if the exam is finalized.',
            ],
          },
          {
            heading: 'With Online + Internal',
            points: [
              'Online attempt total maps to theory_marks.',
              'Teachers can still enter or adjust internal_marks separately.',
              'Published total = theory + internal for portal display.',
            ],
          },
        ],
        [
          'Decide “pass both” vs “total only” before marks entry begins.',
          'Double-check internal max on every subject row in timetable setup.',
        ]
      ),
      g(
        'online-question-bank-papers',
        4,
        'Question Bank & Question Papers',
        'Build the question library and assemble papers for online exams.',
        'Academic → Examinations → Question Bank · Question Papers',
        ['Subjects configured'],
        [
          {
            heading: 'Question Bank',
            points: [
              'Create or import questions by subject.',
              'Types: MCQ, True/False, Fill blank (auto-grade); Short/Long answer (manual).',
              'Set marks per question; maintain options for objective types.',
            ],
          },
          {
            heading: 'Question Papers',
            points: [
              'Create a paper — name, subject, instructions.',
              'Select questions from the bank for that subject.',
              'Only papers linked on an Online timetable slot are used for attempts.',
            ],
          },
        ],
        [
          'Keep one active paper per subject exam slot to avoid student confusion.',
          'Teachers can manage bank/papers for assigned subjects.',
        ]
      ),
      g(
        'student-online-attempt-evaluation',
        5,
        'Student Online Attempts & Online Evaluation',
        'Students take online exams; staff review and adjust marks.',
        'Student → Online Exams · Academic → Online Evaluation',
        ['Online timetable slot with paper', 'Students enrolled'],
        [
          {
            heading: 'Student attempt',
            points: [
              'Student opens Academic → Examinations → Online Exams.',
              'Status: Upcoming → Ongoing → Submitted/Closed.',
              'Start within the schedule window; one attempt; save answers; submit.',
              'Scores stay hidden until the exam results are published.',
            ],
          },
          {
            heading: 'Online Evaluation',
            points: [
              'Staff open Academic → Examinations → Online Evaluation.',
              'Select online schedule → submitted student → review answers.',
              'Edit obtained marks (integers within max); save syncs exam results.',
              'Objective questions are auto-graded; descriptive need manual marks.',
            ],
          },
          {
            heading: 'Parents',
            points: [
              'Parents see timetable and published results only — they cannot take online exams.',
            ],
          },
        ],
        [
          'Finish Online Evaluation before publishing results.',
          'Expired windows auto-submit incomplete attempts where configured.',
        ]
      ),
      g(
        'publish-results-grades',
        6,
        'Publish Results, Grades & Top Performers',
        'Make results live and review grade bands and toppers.',
        'Academic → Examinations → Exam Result · Grade · Top Performers',
        ['Marks entered or online evaluation complete'],
        [
          {
            heading: 'Publish',
            points: [
              'Publish the exam when marks are verified.',
              'Students and parents immediately see published results on their portals.',
            ],
          },
          {
            heading: 'Grade & toppers',
            points: [
              'Grade — configure percentage bands for letter grades.',
              'Top Performers — rank top 3/5/10/etc. by class (teacher-scoped where applicable).',
              'Export reports for report cards as needed.',
            ],
          },
        ],
        ['Do not publish until online descriptive answers are evaluated.']
      ),
    ],
  },

  'assemblies-management': {
    overviewIntro:
      'Schedule assemblies from Academic → Assemblies. Target classes, houses, or groups (from Student Groups), mark attendance, award house points, and run reports.',
    guides: [
      g(
        'create-assembly',
        1,
        'Creating an Assembly',
        'Schedule a school assembly with audience targeting.',
        'Academic → Assemblies → All Assemblies',
        ['Academic year selected'],
        [
          {
            heading: 'Create',
            points: [
              'Click Create Assembly — title, date (not past), start/end time, venue, description.',
              'Choose audience: All Students, Class, Class Section, House, Group, or Student.',
              'For House/Group, select groups from Student Groups.',
              'Save — status starts as Scheduled; participants can be notified.',
            ],
          },
          {
            heading: 'List actions',
            points: [
              'Filter by status; search by title; export Excel/PDF/print.',
              'View opens detail; admins can soft-delete to Trashed.',
            ],
          },
        ],
        ['Create Houses/Groups first if you need house-based assemblies.']
      ),
      g(
        'assembly-detail-agenda',
        2,
        'Detail, Status, Agenda & Points',
        'Update status, build agenda, and award house points.',
        'Academic → Assemblies → View',
        ['Assembly created'],
        [
          {
            heading: 'Status',
            points: [
              'Change status: Scheduled, Ongoing, Completed, Cancelled.',
              'Cancelling can notify the audience.',
            ],
          },
          {
            heading: 'Agenda',
            points: [
              'Add agenda items with title, optional minutes, and description.',
              'Soft-delete agenda rows that are no longer needed.',
            ],
          },
          {
            heading: 'Award points',
            points: [
              'Award points to a house/group with reason — stored on the Student Groups points ledger (source Assembly).',
            ],
          },
        ],
        ['Mark Ongoing on the day, then Completed after attendance is saved.']
      ),
      g(
        'assembly-attendance',
        3,
        'Assembly Attendance',
        'Mark Present, Absent, Late, or Excused for the roster.',
        'Academic → Assemblies → Attendance',
        ['Assembly with student audience'],
        [
          {
            heading: 'Mark attendance',
            points: [
              'Open Attendance for the assembly — roster built from audience targets.',
              'Set status per student or bulk-apply; add remarks if needed.',
              'Save — upserts attendance records for the assembly.',
            ],
          },
        ],
        ['Unmarked rows often default to Present in the UI before save — review carefully.']
      ),
      g(
        'assembly-reports-trash',
        4,
        'Reports & Trashed Assemblies',
        'Summary, attendance, house points reports; restore soft-deleted assemblies.',
        'Academic → Assemblies → Reports · Trashed',
        ['Assemblies recorded'],
        [
          {
            heading: 'Reports',
            points: [
              'Summary — counts by status for the date range / year.',
              'Attendance — aggregates per assembly.',
              'House Points — points totals for House-type groups.',
            ],
          },
          {
            heading: 'Trash',
            points: [
              'Admin soft-deletes assemblies; restore from Trashed Assemblies.',
              'Portal users see All Assemblies and Reports only (no Trashed).',
            ],
          },
        ],
        ['Use House Points report with Student Groups ledger for term house competitions.']
      ),
    ],
  },

  'student-groups': {
    overviewIntro:
      'Manage Houses, Clubs, Teams, and Councils under Academic → Student Groups. Assign members, track points, and use groups in Assemblies.',
    guides: [
      g(
        'create-student-group',
        1,
        'Creating Student Groups',
        'Add houses, clubs, teams, or councils for the school.',
        'Academic → Student Groups',
        ['Headmaster / Admin login'],
        [
          {
            heading: 'Create group',
            points: [
              'Click Create — name (letters/numbers/spaces), type (House, Club, Team, Council, Other).',
              'Optional color, captain label, description; mark Active.',
              'Filter list by type; View opens the detail page.',
            ],
          },
          {
            heading: 'Trash & restore',
            points: [
              'Soft-delete moves the group to Trashed; restore clears deleted state.',
              'Deleting a House clears matching student primary house links.',
            ],
          },
        ],
        [
          'Settings → Houses redirects to Student Groups.',
          'Only Admin / Administrative can create or delete groups.',
        ]
      ),
      g(
        'manage-group-members',
        2,
        'Members & Bulk Assign',
        'Add students with roles; bulk assign by class.',
        'Student Groups → View → Members',
        ['Group created', 'Students enrolled'],
        [
          {
            heading: 'Single & bulk',
            points: [
              'Add one student with role: Member, Captain, Vice Captain, Prefect, or Custom.',
              'Bulk Assign — filter class/section, multi-select students, shared role (up to 500).',
              'Change role inline; remove ends membership (soft).',
            ],
          },
          {
            heading: 'House sync',
            points: [
              'House-type memberships update the student’s primary house on their profile.',
              'Non-house groups do not change students.house_id.',
            ],
          },
        ],
        ['Assign every student to a House before house-based assemblies.']
      ),
      g(
        'group-points-assemblies',
        3,
        'Points Ledger & Assembly Link',
        'Award manual or assembly points; use groups as assembly audiences.',
        'Student Groups → View → Points · Assemblies',
        ['Group with members'],
        [
          {
            heading: 'Points',
            points: [
              'Add manual points with reason (source Manual).',
              'Assemblies can award points with source Assembly.',
              'Ledger shows date, points, reason, source, and who awarded.',
            ],
          },
          {
            heading: 'Assemblies',
            points: [
              'When creating an assembly, audience House or Group uses these memberships.',
              'House Points report under Assemblies aggregates House group ledgers.',
            ],
          },
        ],
        ['Teachers and parents can view groups; only admins award points via UI manage roles.']
      ),
    ],
  },

  'timetable-management': {
    guides: [
      g(
        'configure-time-slots',
        1,
        'Configuring Time Slots',
        'Define school periods once and reuse across all timetables.',
        'Academic → Timetable → Time slots',
        ['School day timings decided'],
        [
          {
            heading: 'Create periods',
            points: [
              'Add each period with label (Period 1, Break, Period 2, etc.).',
              'Set start time and end time for each slot.',
              'Slots apply school-wide for the academic year.',
            ],
          },
        ],
        ['Include lunch and assembly breaks as separate slots for accurate routines.']
      ),
      g(
        'build-class-timetable',
        2,
        'Building Class Timetable',
        'Map subjects and teachers to periods for each class section.',
        'Academic → Timetable → Create timetable',
        ['Time slots configured', 'Class subjects and teacher assignments ready'],
        [
          {
            heading: 'Assign periods',
            points: [
              'Select class and section.',
              'For each day and period, assign subject and teacher.',
              'Assign classroom if rooms are tracked.',
              'Bulk save the weekly grid.',
            ],
          },
        ],
        ['Resolve conflicts before save — same teacher cannot be in two classes at once.']
      ),
      g(
        'section-teacher-routine',
        3,
        'Section & Teacher Routine',
        'Review timetables by section and by teacher for conflict checking.',
        'Academic → Timetable → Section routine / Teacher Timetable',
        ['Timetable saved'],
        [
          {
            heading: 'Review views',
            points: [
              'Section Routine shows one section’s full weekly grid.',
              'Teacher Timetable lists each teacher’s periods across the week.',
              'Teacher Dashboard shows Today’s Routine widget from this data.',
            ],
          },
        ],
        ['Print section routine for classroom display and substitute planning.']
      ),
      g(
        'portal-timetable-access',
        4,
        'Portal Timetable Access',
        'Students, parents, and teachers view routines in their portals.',
        'Academic → Time Table (student/parent/teacher portal)',
        ['Timetable published for class'],
        [
          {
            heading: 'Portal visibility',
            points: [
              'Students see their class timetable under Academic → Time Table.',
              'Parents view selected child’s weekly schedule.',
              'Teachers see personal routine and assigned class periods.',
            ],
          },
        ],
        ['Update timetable when substitutions occur — portals reflect changes immediately.']
      ),
    ],
  },

  'homework-assignments': {
    guides: [
      g(
        'create-homework',
        1,
        'Creating Homework',
        'Publish assignments with due dates, instructions, and attachments.',
        'Academic → Home Work → Add',
        ['Teacher assigned to class-subject', 'Students enrolled'],
        [
          {
            heading: 'New assignment',
            points: [
              'Select class, section, subject, assign date, and due date.',
              'Enter title, description, and homework type (Written, Project, Reading).',
              'Attach files if needed; set max marks for graded work.',
              'Publish — recipients created for all students in the section.',
            ],
          },
        ],
        ['Set realistic due dates — parents monitor deadlines from their portal.']
      ),
      g(
        'track-submissions',
        2,
        'Tracking Submissions',
        'Monitor who submitted and who is pending per assignment.',
        'Academic → Home Work → Assignment detail',
        ['Homework published'],
        [
          {
            heading: 'Submission status',
            points: [
              'Open homework detail to see per-student status: Assigned, Submitted, Evaluated.',
              'Filter homework list by class, subject, and date range.',
              'Export list to Excel or PDF for class register.',
            ],
          },
        ],
        ['Follow up with students showing Assigned past due date.']
      ),
      g(
        'grade-homework',
        3,
        'Grading & Feedback',
        'Enter marks and remarks on submitted homework.',
        'Academic → Home Work → Submissions',
        ['Students submitted work'],
        [
          {
            heading: 'Evaluate work',
            points: [
              'Open submission, enter marks obtained and feedback text.',
              'Mark status Evaluated — visible to student where configured.',
              'Print or export graded register for records.',
            ],
          },
        ],
        ['Consistent grading helps track academic progress alongside formal exams.']
      ),
      g(
        'homework-portal-view',
        4,
        'Student & Parent View',
        'How families see homework lists and due dates.',
        'Academic → Home Work (student/parent portal)',
        ['Homework published'],
        [
          {
            heading: 'Portal experience',
            points: [
              'Students view assigned homework with due dates under Academic.',
              'Parents see child’s homework list on Parent Portal dashboard.',
              'Overdue items stand out for parent follow-up at home.',
            ],
          },
        ],
        ['Teachers should publish homework same day it is assigned in class.']
      ),
    ],
  },

  'meetings-management': {
    overviewIntro:
      'Plan parent–teacher meetings, staff briefings, and department reviews from Academic → Meetings. Schedule with audience targeting, document conclusions, assign follow-up tasks, and keep everyone notified.',
    guides: [
      g(
        'all-meetings-overview',
        1,
        'All Meetings — List & Filters',
        'Browse every scheduled meeting in the school with search, filters, and export.',
        'Academic → Meetings → All Meetings',
        ['Headmaster / Admin or staff login', 'Academic year active'],
        [
          {
            heading: 'Open the meetings list',
            points: [
              'Go to Academic → Meetings → All Meetings from the sidebar.',
              'Table columns: Title, Category, Audience, Date, Time, Status, Action.',
              'Click a meeting title or View to open the detail page.',
            ],
          },
          {
            heading: 'Search & filters',
            points: [
              'Search by meeting title (debounced text search).',
              'Filter by Category: Parent Teacher, Staff, Department, Student, Management, Teacher, General, Other.',
              'Filter by Status: Scheduled, Ongoing, Completed, Cancelled.',
              'Filter by Audience type: All, All Students, Class, Class Section, Student, All Staff, Department, etc.',
            ],
          },
          {
            heading: 'Toolbar actions',
            points: [
              'Refresh — reload the list after changes.',
              'Print, Export PDF, or Export Excel — uses the currently filtered list.',
              'Create Meeting — opens the scheduling form (admin and teacher roles).',
              'Delete — admin only; removes a meeting from All Meetings.',
            ],
          },
        ],
        [
          'Use status filter to find upcoming Scheduled meetings before the week starts.',
          'Export the list for management review or board meeting agendas.',
        ]
      ),
      g(
        'schedule-new-meeting',
        2,
        'Scheduling a New Meeting',
        'Create a meeting with category, audience targeting, date, time, and venue.',
        'Academic → Meetings → Create Meeting',
        ['Classes, sections, and staff records configured'],
        [
          {
            heading: 'Basic details',
            points: [
              'Click Create Meeting on All Meetings or My Meetings.',
              'Enter Title and optional Description.',
              'Pick Meeting Date — cannot be in the past.',
              'Set Start Time and End Time (end must be after start).',
              'Enter Venue (e.g. Conference Hall, Class 5-A, Online link).',
            ],
          },
          {
            heading: 'Category & audience',
            points: [
              'Select one or more Categories (multi-select with All shortcut).',
              'Available audience types change based on category — e.g. Parent Teacher allows Class, Class Section, Student, All Students.',
              'Pick Audience type(s) — broad options (All, All Students, All Staff) need no extra selection.',
              'For specific audiences, select classes, sections, students, departments, designations, or staff as prompted.',
            ],
          },
          {
            heading: 'Teacher restrictions',
            points: [
              'Teachers cannot create Staff, Department, Management, or Teacher-category meetings.',
              'Teachers are limited to class/section/student audiences they are assigned to teach.',
              'Headmaster and Administrative staff have full category and audience access.',
            ],
          },
          {
            heading: 'Save & notify',
            points: [
              'Click Save — meeting appears in All Meetings immediately.',
              'All users in the targeted audience receive a “New meeting scheduled” notification.',
              'Participants open All Meetings to view date, time, venue, and description.',
            ],
          },
        ],
        [
          'For a whole-school assembly, use General category with All audience.',
          'For a single-class PTM, use Parent Teacher + Class Section targeting.',
        ]
      ),
      g(
        'my-meetings-teacher',
        3,
        'My Meetings (Teacher View)',
        'Teachers track meetings they created and manage their own schedules.',
        'Academic → Meetings → My Meetings',
        ['Teacher login'],
        [
          {
            heading: 'Personal meeting list',
            points: [
              'Open Academic → Meetings → My Meetings.',
              'Shows only meetings where you are the creator.',
              'Same filters as All Meetings: search, category, status, audience.',
              'Extra column: Created By (your name on every row).',
            ],
          },
          {
            heading: 'Create from My Meetings',
            points: [
              'Teachers click Create Meeting here to schedule a class PTM or student meeting.',
              'New meeting appears in both My Meetings and All Meetings (visible to targeted audience).',
              'Edit and status changes available on the meeting detail page for meetings you created.',
            ],
          },
          {
            heading: 'Export',
            points: [
              'Print or export your personal meeting list for weekly planning.',
            ],
          },
        ],
        [
          'Use My Meetings to quickly find PTMs you scheduled without scrolling the full school list.',
          'Teachers cannot delete meetings — contact admin if a meeting must be removed.',
        ]
      ),
      g(
        'meeting-detail-status',
        4,
        'Meeting Detail, Status & Conclusions',
        'View full meeting info, update status, record conclusions, and edit details.',
        'Academic → Meetings → All Meetings → View',
        ['Meeting created', 'Access to the meeting (creator, admin, or targeted participant)'],
        [
          {
            heading: 'Meeting information card',
            points: [
              'Shows Category, Audience, Date, Time, Status badge, Venue, and Description.',
              'Targets list — each class, section, student, department, or staff member included.',
              'Back arrow returns to All Meetings.',
            ],
          },
          {
            heading: 'Update status',
            points: [
              'Creator or admin clicks Status dropdown on the detail page.',
              'Options: Scheduled → Ongoing → Completed → Cancelled.',
              'Confirm the change — status badge updates on the list and detail views.',
            ],
          },
          {
            heading: 'Meet Conclusions',
            points: [
              'Editable textarea for meeting minutes, decisions, and action points.',
              'Click Save Conclusions — stored on the meeting record.',
              'Participants see conclusions as read-only text after save.',
            ],
          },
          {
            heading: 'Edit meeting',
            points: [
              'Edit button opens the same create form pre-filled with current values.',
              'Admin can edit any meeting; teachers can edit only meetings they created.',
              'Saving sends a “Meeting updated” notification to the audience.',
            ],
          },
        ],
        [
          'Mark Completed only after the meeting has taken place.',
          'Record conclusions while details are fresh — assign tasks on the same page.',
        ]
      ),
      g(
        'assign-meeting-tasks',
        5,
        'Assigning Meeting Tasks',
        'Create follow-up tasks for students or staff directly from a meeting.',
        'Meeting Detail → Assigned Tasks section',
        ['Meeting detail open', 'Creator or admin access'],
        [
          {
            heading: 'Add a task',
            points: [
              'Scroll to Assigned Tasks on the meeting detail page.',
              'Enter Task title (required) and optional Description.',
              'Pick Due date — cannot be in the past.',
              'Choose assignee type: Student or Staff / Teacher.',
              'Search and select the assignee from the dropdown (filtered by meeting audience).',
              'Click Assign Task — task appears in the table below.',
            ],
          },
          {
            heading: 'Task table on detail page',
            points: [
              'Columns: Title, Assignee (with Student/Staff label), Due date, Status.',
              'Admin, teacher, or staff assignee can change status via dropdown: Pending, In Progress, Completed.',
              'Students and parents see status as a read-only badge.',
            ],
          },
          {
            heading: 'Notifications',
            points: [
              'Assignee receives “Meeting task assigned” notification.',
              'For student tasks, linked parent/guardian accounts are also notified.',
              'Notification links to Assigned Tasks page.',
            ],
          },
        ],
        [
          'Assign tasks before closing the meeting — keeps follow-up accountable.',
          'Tasks cannot be assigned to guardians directly; assign to the student instead.',
        ]
      ),
      g(
        'assigned-tasks-tracker',
        6,
        'Assigned Tasks — Cross-Meeting Tracker',
        'View and update all meeting tasks assigned to you or your school in one place.',
        'Academic → Meetings → Assigned Tasks',
        ['At least one meeting task assigned'],
        [
          {
            heading: 'Task list',
            points: [
              'Open Academic → Meetings → Assigned Tasks.',
              'Columns: Task, Meeting (linked), Assigned To (admin view only), Due Date, Status.',
              'Search by task title or meeting name.',
              'Filter by status: Pending, In Progress, Completed.',
            ],
          },
          {
            heading: 'Update status',
            points: [
              'Teachers, admins, and staff assignees change status via inline dropdown.',
              'Students, parents, and guardians see read-only status badges.',
              'Click the Meeting link to open the parent meeting detail page.',
            ],
          },
          {
            heading: 'Who sees what',
            points: [
              'Admins see all tasks across all meetings with Assigned To column.',
              'Teachers see tasks they assigned plus tasks assigned to them or their students.',
              'Students and parents see only tasks assigned to them or their ward.',
              'Staff see tasks assigned to their staff record.',
            ],
          },
        ],
        [
          'Review Pending tasks weekly to ensure PTM action items are not forgotten.',
          'Use status In Progress when work has started but is not yet finished.',
        ]
      ),
      g(
        'meetings-portals-notifications',
        7,
        'Portals, Visibility & Notifications',
        'How students, parents, staff, and teachers experience meetings outside the admin view.',
        'Academic → Meetings (all roles)',
        ['Meetings scheduled with correct audience targeting'],
        [
          {
            heading: 'Sidebar by role',
            points: [
              'Admin / Headmaster / Teacher: All Meetings, My Meetings, Assigned Tasks.',
              'Student / Parent / Guardian / Staff portal: All Meetings, Assigned Tasks (no My Meetings).',
              'Each user sees only meetings targeted to their profile, class, or department.',
            ],
          },
          {
            heading: 'Student & parent experience',
            points: [
              'Open Academic → Meetings → All Meetings to see upcoming PTMs and school gatherings.',
              'View meeting date, time, venue, and conclusions on the detail page.',
              'Check Assigned Tasks for action items from PTMs (read-only status).',
            ],
          },
          {
            heading: 'Staff experience',
            points: [
              'Staff targeted in a meeting see it under All Meetings.',
              'Staff-assigned tasks appear in Assigned Tasks with status update permission.',
            ],
          },
          {
            heading: 'Notification routing',
            points: [
              '“New meeting scheduled” → opens All Meetings.',
              '“Meeting updated” → opens All Meetings.',
              '“Meeting task assigned” → opens Assigned Tasks.',
              'Notifications appear in the bell icon on every role’s dashboard.',
            ],
          },
        ],
        [
          'Target audiences precisely — overly broad “All” meetings notify every user in the school.',
          'Share institute login details with parents so they receive PTM notifications on day one.',
        ]
      ),
    ],
  },

  'payroll-management': {
    overviewIntro:
      'Process staff salaries from HRM → Payroll. Define salary components, assign pay structures to teachers and staff, generate monthly payslips, mark payments, and let employees view their own salary slips.',
    guides: [
      g(
        'salary-components-setup',
        1,
        'Salary Components Setup',
        'Create reusable earnings and deductions used across all staff payroll.',
        'HRM → Payroll → Manage Components',
        ['Headmaster / Admin login', 'HRM module enabled'],
        [
          {
            heading: 'Open salary components',
            points: [
              'Go to HRM → Payroll and click Manage Components (top-right).',
              'Table lists Component Name, Type (Earning or Deduction), and Description.',
              'These components are assigned per teacher/staff when setting up salary.',
            ],
          },
          {
            heading: 'Add a component',
            points: [
              'Click Add Component — enter name (e.g. HRA, Transport Allowance, PF, Professional Tax).',
              'Select Type: Earning (Allowance) for amounts added to pay, or Deduction for amounts subtracted.',
              'Add optional description for HR reference.',
              'Save — component appears in teacher/staff salary assignment forms.',
            ],
          },
          {
            heading: 'Edit or remove',
            points: [
              'Click edit icon to update name, type, or description.',
              'Delete deactivates a component no longer in use.',
              'Existing payslip snapshots keep historical amounts even if a component is later changed.',
            ],
          },
        ],
        [
          'Set up standard components (Basic, HRA, DA, PF, Tax) before adding teachers.',
          'Use clear names — they appear on printed salary slips.',
        ]
      ),
      g(
        'staff-salary-assignment',
        2,
        'Assigning Salary to Teachers & Staff',
        'Configure basic salary, bank details, and component amounts for each employee.',
        'People → Teachers · HRM → Staffs',
        ['Salary components created', 'Departments and designations configured'],
        [
          {
            heading: 'Teacher salary setup',
            points: [
              'Open People → Teachers → Add Teacher or edit an existing teacher.',
              'Scroll to the Payroll section — enter Basic Salary.',
              'Toggle earnings (allowances) and deductions; enter monthly amount for each selected component.',
              'Fill bank account details (account name, number, IFSC, branch) for salary transfer records.',
              'Save — creates an active salary assignment linked to the teacher.',
            ],
          },
          {
            heading: 'Staff salary setup',
            points: [
              'Open HRM → Staffs → Add Staff or edit an existing staff record.',
              'Enter Basic Salary, EPF No, PAN, contract type, shift, and work location.',
              'Add bank account details under the Bank section.',
              'Save — salary assignment is created when payroll fields are provided.',
            ],
          },
          {
            heading: 'Before generating payroll',
            points: [
              'Each active staff member needs a salary assignment valid for the target month.',
              'If generation reports no staff found, verify basic salary is saved on the staff/teacher profile.',
            ],
          },
        ],
        [
          'Update salary assignments before generating payroll for a new month.',
          'Teachers support full component breakdown; ensure amounts match your school pay policy.',
        ]
      ),
      g(
        'generate-monthly-payroll',
        3,
        'Generating Monthly Payroll',
        'Create Draft payslips for all eligible staff for a selected month.',
        'HRM → Payroll → Generate Payroll',
        ['Salary components configured', 'Staff salary assignments saved'],
        [
          {
            heading: 'Start generation',
            points: [
              'Open HRM → Payroll from the sidebar.',
              'Click Generate Payroll in the Payroll List card.',
              'Select Month (January–December) and Year in the dialog.',
              'Confirm — system processes all active staff with valid salary assignments.',
            ],
          },
          {
            heading: 'What the system calculates',
            points: [
              'Gross = Basic Salary + sum of earning/allowance components.',
              'Net = Gross − sum of deduction components.',
              'Each payslip is saved with status Draft and a snapshot of components at generation time.',
              'Staff who already have a payslip for that month are skipped automatically.',
            ],
          },
          {
            heading: 'If generation fails',
            points: [
              'Message appears when no active staff have salary settings for the selected period.',
              'Add or update salary on teacher/staff profiles, then retry generation.',
            ],
          },
        ],
        [
          'Generate payroll at month-end after attendance and leave are finalized.',
          'Run generation once per month — duplicate payslips for the same period are prevented.',
        ]
      ),
      g(
        'payroll-list-filters',
        4,
        'Payroll List, Search & Export',
        'Browse, filter, and export all generated payslips.',
        'HRM → Payroll',
        ['At least one payroll generated'],
        [
          {
            heading: 'Payroll table',
            points: [
              'Columns: Employee ID, Name, Month, Department, Designation, Phone, Net Amount, Status.',
              'Status badge: Draft (amber) for pending, Paid (green) for settled.',
              'Table supports row selection for bulk actions.',
            ],
          },
          {
            heading: 'Filters',
            points: [
              'Filter dropdown — narrow by Staff Name, Month, or Year.',
              'Click Apply to filter; Reset clears all filters.',
              'Date range picker available for period-based review.',
            ],
          },
          {
            heading: 'Toolbar',
            points: [
              'Refresh — reload list after generation or status changes.',
              'Print, Export PDF, or Export Excel — exports the full payroll list.',
              'Manage Components — shortcut to salary component settings.',
            ],
          },
        ],
        [
          'Filter by month before bank transfer to get the exact disbursement list.',
          'Export Excel for accounts team reconciliation.',
        ]
      ),
      g(
        'payslip-view-print',
        5,
        'Viewing & Printing Payslips',
        'Open a salary slip with full earnings/deductions breakdown and print or download.',
        'HRM → Payroll → View Payslip',
        ['Payslip generated'],
        [
          {
            heading: 'Open a payslip',
            points: [
              'On the Payroll list, click View Payslip on any row.',
              'Salary Slip page shows school branding, employee details, and pay period.',
              'Employee section: name, employee code, department, designation.',
            ],
          },
          {
            heading: 'Earnings & deductions',
            points: [
              'Earnings table: Basic Salary plus each allowance with amount.',
              'Deductions table: each deduction component with amount.',
              'Net Pay displayed at the bottom after all calculations.',
            ],
          },
          {
            heading: 'Print & PDF',
            points: [
              'Click Print to open a print-friendly view.',
              'Download PDF for email distribution or records.',
              'Back link returns to HRM → Payroll list.',
            ],
          },
        ],
        [
          'Review a sample payslip before marking the whole batch as Paid.',
          'Share printed slips with staff who do not use the portal.',
        ]
      ),
      g(
        'payroll-status-payments',
        6,
        'Marking Paid & Bulk Actions',
        'Update payment status after salary disbursement and manage draft payslips.',
        'HRM → Payroll → Action column',
        ['Draft payslips generated'],
        [
          {
            heading: 'Mark single payslip paid',
            points: [
              'On a Draft row, click Mark Paid — confirm the action.',
              'Status changes to Paid (green badge).',
              'Paid payslips cannot be deleted.',
            ],
          },
          {
            heading: 'Revert to draft',
            points: [
              'On a Paid row, click Mark Draft to revert if marked paid by mistake.',
              'Use only for corrections before month-end close.',
            ],
          },
          {
            heading: 'Delete draft payslip',
            points: [
              'Delete button available only on Draft (unpaid) rows.',
              'Permanently removes the payslip — regenerate if needed.',
            ],
          },
          {
            heading: 'Bulk actions',
            points: [
              'Select multiple rows using checkboxes.',
              'Mark Selected Paid — batch-update all selected to Paid.',
              'Delete Selected — removes unpaid payslips only; paid rows are skipped.',
            ],
          },
        ],
        [
          'Mark Paid only after bank transfer or cash disbursement is confirmed.',
          'Match paid count with bank statement before month-end accounts close.',
        ]
      ),
      g(
        'staff-payslip-self-service',
        7,
        'Teacher & Staff Payslip Access',
        'How employees view their own salary history securely.',
        'People → Teachers → Teacher Salary',
        ['Teacher/staff login with linked staff record'],
        [
          {
            heading: 'Teacher salary page',
            points: [
              'Teacher logs in → People → Teachers → open own profile → Teacher Salary tab.',
              'Shows current salary breakdown: basic, allowances, deductions, gross, and net.',
              'Payslip history table: ID, Salary For (month), Date, Payment Method, Net Salary.',
              'Click View Payslip on any row to open the printable salary slip.',
            ],
          },
          {
            heading: 'Access control',
            points: [
              'Teachers and staff see only their own payslips — not other employees.',
              'Headmaster / Admin sees all payslips on HRM → Payroll.',
              'Server enforces ownership check on every payslip view request.',
            ],
          },
          {
            heading: 'Librarian & other staff',
            points: [
              'Staff roles with salary assignments follow the same self-service pattern on their profile.',
              'Payslip list loads from the same payroll engine as the admin view.',
            ],
          },
        ],
        [
          'Share teacher login credentials during onboarding so staff can download payslips.',
          'Admins process payroll; staff only view — separation keeps payroll secure.',
        ]
      ),
    ],
  },

  'parent-portal': {
    guides: [
      g(
        'parent-account-setup',
        1,
        'Parent Account Setup',
        'Link parent login to student during admission.',
        'People → Students → Add Student (parent section)',
        ['Student admission in progress'],
        [
          {
            heading: 'Create parent access',
            points: [
              'During Add Student, enter parent/guardian phone and email.',
              'System generates parent username and password linked to the student.',
              'Share institute number + credentials securely with the family.',
            ],
          },
        ],
        ['One parent account can link to multiple children — child switcher on dashboard.']
      ),
      g(
        'parent-dashboard',
        2,
        'Parent Dashboard',
        'Overview of attendance, fees, notices, and child switcher.',
        'Parent login → Dashboard',
        ['Parent account active'],
        [
          {
            heading: 'Dashboard widgets',
            points: [
              'See attendance percentage and pending fees at a glance.',
              'New notices and upcoming events appear on the dashboard.',
              'Switch between children if more than one is enrolled.',
            ],
          },
        ],
        ['Encourage parents to check dashboard weekly — reduces office phone queries.']
      ),
      g(
        'parent-academic-monitoring',
        3,
        'Academic Monitoring',
        'View timetable, homework, syllabus, exam results, and leave.',
        'Parent Portal → Academic menu',
        ['School publishing data for the session'],
        [
          {
            heading: 'Academic menus',
            points: [
              'Child Profile, Subjects, Syllabus, Time Table, and Homework.',
              'Apply for leave on behalf of child under Leave & Attendance.',
              'View Exam Timetable and Results when school publishes them.',
            ],
          },
        ],
        ['All data is scoped — parents never see other students’ records.']
      ),
      g(
        'parent-fees-notices',
        4,
        'Fees & School Notices',
        'Check dues, payment history, and announcements.',
        'Parent Portal → Fees / Notice Board',
        ['Fee structure assigned', 'Notices published by school'],
        [
          {
            heading: 'Fees and communication',
            points: [
              'Fees screen shows payable, paid, balance, and receipts.',
              'Notice Board lists PTM dates, holidays, and school announcements.',
              'Events calendar shows sports day, exams, and cultural programs.',
            ],
          },
        ],
        ['Fee reminders on dashboard reduce last-minute office visits.']
      ),
    ],
  },

  'teacher-portal': {
    guides: [
      g(
        'teacher-first-login',
        1,
        'Teacher First Login',
        'Access the teacher workspace with institute number and credentials.',
        'Login page → Teacher Dashboard',
        ['Teacher account created by admin', 'Class assignments configured'],
        [
          {
            heading: 'Getting started',
            points: [
              'Login with institute number, teacher username, and password.',
              'Land on Teacher Dashboard — not the full admin sidebar.',
              'Change password from Profile after first login.',
            ],
          },
        ],
        ['Teachers without assignments see limited menus until admin completes Teacher Assignments.']
      ),
      g(
        'teacher-dashboard-routine',
        2,
        'Dashboard & Daily Routine',
        'Today’s periods, class size, and pending tasks at a glance.',
        'Teacher Dashboard / Teacher Timetable',
        ['Timetable built for teacher'],
        [
          {
            heading: 'Daily workflow',
            points: [
              'Check Today’s Routine widget before first period.',
              'View class size and today’s attendance summary on dashboard.',
              'See pending leave requests requiring class teacher approval.',
            ],
          },
        ],
        ['Star students and performance widgets highlight class achievers.']
      ),
      g(
        'teacher-attendance-exams',
        3,
        'Attendance, Homework & Exams',
        'Core teaching tasks from the teacher portal.',
        'Academic → Attendance / Home Work / Exam Result',
        ['Teacher assigned to classes'],
        [
          {
            heading: 'Daily teaching ops',
            points: [
              'Mark attendance for assigned classes only.',
              'Create and grade homework for your subjects.',
              'Enter exam marks for permitted subjects.',
            ],
          },
        ],
        ['Teachers cannot access admin-only modules like Fees Collection or school-wide settings.']
      ),
      g(
        'teacher-class-students',
        4,
        'Class Students & Communication',
        'View students, parents, notices, and leave approvals.',
        'People → Students / Notice Board',
        ['Class teacher or subject teacher assignment'],
        [
          {
            heading: 'People and comms',
            points: [
              'Students and Parents lists scoped to your classes.',
              'Approve student leave requests for assigned classes.',
              'Read Notice Board and Events for school updates.',
              'Generate Bonafide for students in your class where allowed.',
            ],
          },
        ],
        ['Student Attendance Report helps identify chronic absentees early.']
      ),
    ],
  },

  'transport-management': {
    guides: [
      g(
        'routes-pickup-points',
        1,
        'Routes & Pickup Points',
        'Define transport routes and stops with pickup times.',
        'Management → Transport → Routes / Pickup Points',
        ['Academic year selected'],
        [
          {
            heading: 'Route setup',
            points: [
              'Create Pickup Points with name, address, landmark, and sequence.',
              'Create Routes with route name, code, and pickup/drop time windows.',
              'Link stops to routes with order index and per-stop pickup time.',
            ],
          },
        ],
        ['Sequence order determines bus pickup order — plan for traffic patterns.']
      ),
      g(
        'vehicles-drivers',
        2,
        'Vehicles & Drivers',
        'Register fleet and assign drivers to routes.',
        'Management → Transport → Vehicle / Transport Staff',
        ['Routes created'],
        [
          {
            heading: 'Fleet registry',
            points: [
              'Add vehicles with registration number, type, capacity, and status.',
              'Register Transport Staff (drivers) in HR/staff module.',
              'Assign Vehicle links route + vehicle + driver for the academic year.',
            ],
          },
        ],
        ['Track seating capacity — do not over-allocate students beyond vehicle capacity.']
      ),
      g(
        'transport-allocation',
        3,
        'Student Transport Allocation',
        'Assign students to route, pickup point, and fee plan.',
        'Management → Transport → Transport Allocation',
        ['Students enrolled', 'Routes and vehicles active'],
        [
          {
            heading: 'Allocate students',
            points: [
              'Search student and select route, pickup point, and vehicle assignment.',
              'Set assigned transport fee amount from fee master.',
              'View route-wise student list for daily planning.',
            ],
          },
        ],
        ['Update allocation when a student changes address mid-year.']
      ),
      g(
        'transport-fees',
        4,
        'Transport Fees',
        'Link transport charges to student fee statements.',
        'Management → Transport → Transport Fees',
        ['Fee collection module active'],
        [
          {
            heading: 'Fee integration',
            points: [
              'Configure Transport Fee Master by pickup point or plan name.',
              'Allocated amount flows into student fee balance.',
              'Collect via standard Collect Fees screen alongside tuition.',
            ],
          },
        ],
        ['Optional transport fee types keep tuition separate from route charges.']
      ),
    ],
  },

  'library-management': {
    guides: [
      g(
        'library-catalog',
        1,
        'Library Catalog Setup',
        'Add categories, books, and individual copies.',
        'Management → Library → Books / Categories',
        ['Library module enabled'],
        [
          {
            heading: 'Build catalog',
            points: [
              'Create Library Categories (Textbooks, Fiction, Reference).',
              'Add Books with title, author, ISBN, publisher, and category.',
              'Add book copies with accession number and condition — each copy issuable separately.',
            ],
          },
        ],
        ['Accession numbers must be unique — use school prefix (e.g. DPS-LIB-00001).']
      ),
      g(
        'library-members',
        2,
        'Library Members',
        'Register students and staff as library members.',
        'Management → Library → Library Members',
        ['Students/staff in system'],
        [
          {
            heading: 'Member registration',
            points: [
              'Register member from student or staff record.',
              'System generates library card number per academic year.',
              'Set member status active/inactive.',
            ],
          },
        ],
        ['Register only active students to avoid issuing books to withdrawn pupils.']
      ),
      g(
        'issue-return',
        3,
        'Issue & Return Books',
        'Daily circulation workflow at the librarian desk.',
        'Management → Library → Issue Book / Return',
        ['Books and members registered', 'Lending policy configured'],
        [
          {
            heading: 'Circulation',
            points: [
              'Issue Book — select member and available copy; due date auto-calculated from policy.',
              'Return — scan or select issued book; copy becomes available again.',
              'Overdue tracking visible on issue list.',
            ],
          },
        ],
        ['Policies define max books and loan period — enforced at issue time.']
      ),
      g(
        'reservations-policies',
        4,
        'Reservations & Policies',
        'Queue for borrowed books and configure lending rules.',
        'Management → Library → Policies / Reservations',
        ['Catalog with issued books'],
        [
          {
            heading: 'Policies and holds',
            points: [
              'Create policy with issue duration, max books allowed, and audience (Student/Staff).',
              'Students reserve books currently on loan — status Pending until available.',
              'Expiration date auto-set on reservations.',
            ],
          },
        ],
        ['Fine rules in policy support future fee integration for lost books.']
      ),
    ],
  },

  'hostel-management': {
    guides: [
      g(
        'hostel-buildings',
        1,
        'Hostel Buildings & Floors',
        'Register hostels with warden and capacity.',
        'Management → Hostel → Hostel List / Floors',
        ['Hostel module enabled'],
        [
          {
            heading: 'Hostel registry',
            points: [
              'Add Hostel with name, code, gender (boys/girls), address, and warden.',
              'Add Floors under each hostel with floor name and number.',
              'Set intake capacity for planning.',
            ],
          },
        ],
        ['Separate boys and girls hostels keep assignment and reporting clear.']
      ),
      g(
        'rooms-beds',
        2,
        'Rooms, Types & Beds',
        'Build room inventory down to individual bed level.',
        'Management → Hostel → Room Types / Rooms / Beds',
        ['Hostel and floors created'],
        [
          {
            heading: 'Room hierarchy',
            points: [
              'Define Room Types (Single, Double, Triple) with amenities flags.',
              'Add Hostel Rooms with room number, type, monthly rent, and status.',
              'Create Beds per room — each bed tracked as available or occupied.',
            ],
          },
        ],
        ['Bed-level tracking prevents double assignment in the same room.']
      ),
      g(
        'hostel-assignment',
        3,
        'Assigning Students to Beds',
        'Link boarding students to specific beds.',
        'Management → Hostel → Hostel Assignments',
        ['Available beds', 'Students enrolled'],
        [
          {
            heading: 'Assignment workflow',
            points: [
              'Select student, hostel, floor, room, and available bed.',
              'Set assigned date and security deposit amount.',
              'Bed status changes to occupied automatically.',
            ],
          },
        ],
        ['Filter beds by availability before admission season rush.']
      ),
      g(
        'checkout-occupancy',
        4,
        'Checkout & Occupancy',
        'Vacate beds and monitor hostel occupancy.',
        'Management → Hostel → Assignments / Beds list',
        ['Active assignments'],
        [
          {
            heading: 'Vacate and monitor',
            points: [
              'Checkout student when they leave hostel — bed returns to available.',
              'Cancel or change assignment when swapping rooms.',
              'Occupancy filters show which students occupy which rooms.',
            ],
          },
        ],
        ['Run occupancy report before each term to plan room allocation for new admissions.']
      ),
    ],
  },
};
